import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import * as Yup from 'yup';
import { submitReRegistrationService } from "../services/ReRegistrationService";
import { setReRegistrationsReload } from "../store/actions";
import { brand } from "./Constants";

/**
 * Gets different Learning Materials List based on 2 different scenarios:
 * 1. If user selected an option, and it has learning methods.
 * 2. For selecting items in Review & Submit, return selectable learning material options.
 * @param {*} learningMaterialsOptions 
 * @param {*} curArtOption 
 * @returns 
 */
export function getSelectableLearningMaterialsList(learningMaterialsOptions, curArtOption) {
    if (curArtOption && curArtOption !== 'other' && curArtOption.learningMethods?.length > 0) {
        const curOptionLearningMethods = curArtOption.learningMethods; // Array like ["full", "parttimeonline" ...]
        // Filter options based on selected learning methods
        return learningMaterialsOptions.filter(option => curOptionLearningMethods.includes(option.value));
    } else {
        return learningMaterialsOptions;
    }
}

/**
 * Gets different Campus List based on 2 different scenarios:
 * 1. If the user selected an option, and it has campuses (curArtOption.campuses).
 * 2. For selecting items in Review & Submit, return only unique campuses.
 * @param {object} campusOptions Fetched campuses (Source of truth saved in state).
 * @param {*} curArtOption Currently selected option by the user.
 */
export function getSelectableCampusList(campusOptions, curArtOption) {
    if (curArtOption && curArtOption.campuses && curArtOption.campuses.length > 0) {
        const curOptionCampuses = curArtOption.campuses;
        const curOptionCampusSlugs = curOptionCampuses.map(item => item.slug);
        // Filter options based on selected learning methods
        return campusOptions?.filter(option => curOptionCampusSlugs.includes(option?.value));
    } else {
        // Filter campusOptions to have only unique labels since we have multiple with same label but diff value
        return [...new Map(campusOptions.map(item => [item.label, item])).values()];
    }
}

/**
 * Check if articulation is submitted.
 * @param {object} registration To check status.
 * @returns true submitted.
 */
export function isSubmitted(registration) {
    if (registration && registration.status.toLowerCase() === 'submitted') {
        return true;
    }
    return false;
}

/**
 * Check if articulation should be disabled.
 * @param {object} registration To check reg_status.
 * @returns true disabled.
 */
export function isDisabled(registration) {
    if (isSubmitted(registration)) {
        return true;
    }
    if (registration && registration.reg_status && registration.reg_status.toLowerCase() === 'open') {
        return false;
    }
    return true;
}

/**
 * Take the smallest price out of fullTimeHybridFee, fullTimeOnlineFee and partTimeOnlineFee.
 * Format R p/m.
 * @param {*} param0 
 * @returns 
 */
export function getFormattedArticulationPrice({ fullTimeHybridFee = 0, fullTimeOnlineFee = 0, partTimeOnlineFee = 0 }) {
    const smallestFee = Math.min(fullTimeHybridFee, fullTimeOnlineFee, partTimeOnlineFee) / 12;

    const formattedFee = Math.floor(smallestFee).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `R ${formattedFee}p/m`;
};

/**
 * Extracts data from articulationOptionSelected, adds `isOther` if:
 * 1. Option was explicitly selected by the user (articulationOptionSelected = "other").
 * 2. articulationReadOnly.progression = -1 (after submission)
 * @param {*} option 
 * @param {*} articulationReadOnly 
 * @returns 
 */
export function getArticulationData(option, articulationReadOnly) {
    if (option === "other"
        || (articulationReadOnly && Number(articulationReadOnly.progression) === -1)) {
        return {
            isOther: true,
            categoryColor: "",
            credits: "",
            description: "",
            name: "",
            saqaId: "",
            school: "",
            type: "",
            url: ""
        };
    }

    const {
        categoryColor = "",
        credits = "",
        description = "",
        name = "",
        saqaId = "",
        school = "",
        type = "",
        url = ""
    } = option || {};

    return { categoryColor, credits, description, name, saqaId, school, type, url };
}

function getLateReReg({ late_re_reg_end_date }) {
    const currentDate = new Date();
    const lateReRegEndDate = new Date(late_re_reg_end_date * 1000);

    const lateReg = currentDate > lateReRegEndDate;
    return lateReg;
}

/**
 * formik.values is the source of truth, but articulationReadOnly takes preference (if submitted).
 * @param {*} articulationReadOnly Only if submitted already.
 * @param {*} qualificationDetails Default
 * @returns 
 */
function getInitValues(submitted, articulationReadOnly, qualificationDetails) {
    const defaultLearningMethod = qualificationDetails?.type || "";
    const defaultCampus = qualificationDetails?.campus || "";
    if (submitted && articulationReadOnly) {
        return {
            learningMethod: articulationReadOnly.learningMethod || defaultLearningMethod,
            campus: articulationReadOnly.campus || defaultCampus
        };
    }
    return {
        learningMethod: defaultLearningMethod,
        campus: defaultCampus
    };
}

function getPayloadParams(articulationOptionSelected, payloadData, lateReg, brand, isOther, values) {
    const progression = isOther
        ? -1
        : (articulationOptionSelected.netsuiteId ? articulationOptionSelected.netsuiteId?.toString() : -1);
    return {
        progression: progression,
        // School of Artisans. IT: Visual Art & Visual Design. MappingSchool (SP). 
        latest_enrolment_netsuite_id: payloadData.registrationFullData?.currentEnrolmentNetsuiteId,
        rereg_academy: isOther ? payloadData.registration.academy : articulationOptionSelected.school,
        rereg_id: payloadData.registration.id,
        late_reg: lateReg,// Or payloadData.lateReg
        brand: brand,
        is_bachelor_form: 1,
        isGraduate: 1,
        qual_name: isOther ? 'Other' : articulationOptionSelected.name,
        student_type: 1,// 1 = Articulation.
        delivery_methode: values.learningMethod,
        campus: values.campus
    };
}

/**
 * Custom hook to abstract form.
 * Use to submit form.
 * @param {*} qualificationDetails Detail from ReRegistration to use for learningMethod and campus.
 * @param {*} payloadData Various things needed for submission of articulation.
 * @param {*} isOther If Other Articulation Option was selected in Step 1.
 * @returns 
 */
export function useArticulationForm(
    articulationReadOnly,
    articulationOptionSelected,
    disabled,
    qualificationDetails,
    payloadData,
    submitted,
    isOther
) {
    const history = useHistory();
    const dispatch = useDispatch();
    const reload = useSelector(state => state.registrations.reload);
    const [isLoading, setIsLoading] = useState(false);
    const disableInputs = disabled || submitted || isLoading;

    const launchSchema = Yup.object({
        learningMethod: Yup.string().required("Learning method is required"),
        campus: Yup.string().required("Campus is required")
    });

    const initialValue = getInitValues(submitted, articulationReadOnly, qualificationDetails);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValue,
        validationSchema: launchSchema,
        onSubmit: async (values) => {
            setIsLoading(true);

            // Show loading modal immediately
            Swal.fire({
                allowEscapeKey: false,
                allowOutsideClick: false,
                title: "Submitting",
                onOpen: function () {
                    Swal.showLoading();
                },
            });

            const lateReg = getLateReReg(payloadData.registration);
            const params = getPayloadParams(articulationOptionSelected, payloadData, lateReg, brand, isOther, values);
            try {
                const res = await submitReRegistrationService(params);
            
                // Close the loading modal
                Swal.close();

                if (res) {
                    formik.resetForm();
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: "Submitted Articulation Options",
                    }).then(() => {
                        dispatch(setReRegistrationsReload(!reload));
                        history.push("/reregStatus");
                    });
                }
            } catch (error) {
                console.error(error);
                
                // First close the loading modal
                Swal.close();
                Swal.fire({
                    icon: "error",
                    title: "Submission Failed",
                    text: error.message || "An error occurred during submission"
                });
            } finally {
                setIsLoading(false);
            }
        }
    });

    return { disableInputs, formik, isLoading };
}

/**
 * If submitted, try to manually set the articulationOptionSelected (for display purposes).
 * @param {*} submitted Submission submitted.
 * @param {*} articulationReadOnly Data from API for readOnly purposes.
 * @param {*} articulationOptions Fetched Articulation Options (via pmcId).
 * @param {*} setArticulationOption Set function to manually set displayable articulation option.
 */
export function useSubmittedReadOnlyData(submitted, articulationReadOnly, articulationOptions, setArticulationOption) {
    useEffect(() => {
        if (submitted && articulationReadOnly && articulationOptions.length > 0) {
            if (articulationReadOnly.progression) {
                const submittedArticulation = articulationOptions.find((x) => {
                    if (x.netsuiteId) {
                        return Number(x.netsuiteId) === Number(articulationReadOnly.progression)
                    }
                    return false;
                });
                if (submittedArticulation) {
                    setArticulationOption(submittedArticulation);
                }
            }
        }
    }, [submitted, articulationReadOnly, articulationOptions]);
}
